import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StarRating from "../components/StarRating";
import ReviewComposer from "../components/ReviewComposer";
import VerifiedBadge from "../components/VerifiedBadge";
import TrustScoreBadge from "../components/TrustScoreBadge";
import TrustScoreTrendCard from "../components/TrustScoreTrendCard";
import ProviderAnalyticsCard from "../components/ProviderAnalyticsCard";
import VerifiedTierBadge from "../components/VerifiedTierBadge";
import TrustStatusCard from "../components/TrustStatusCard";
import LineageEditor from "../components/LineageEditor";
import EndorseDialog from "../components/EndorseDialog";
import ImageUploader from "../components/ImageUploader";
import ConnectStripeButton from "../components/ConnectStripeButton";
import BookingSlotPicker from "../components/BookingSlotPicker";
import FriendButton from "../components/FriendButton";
import PostCard from "../components/PostCard";
import PostComposer from "../components/PostComposer";
import CategorySelect from "../components/CategorySelect";
import ElderBadge, { ElderAvatarRing } from "../components/ElderBadge";
import { hideOnError } from "../lib/imgFallback";
import {
  Phone, Video, MessageSquare, Edit, Camera, Calendar, MapPin,
  Sparkles, Users, FileText, ShoppingBag, Star, Trash2, Zap, Clock, Crown,
} from "lucide-react";
import { toast } from "sonner";
import BoostDialog from "../components/BoostDialog";
import AvailabilityEditor from "../components/AvailabilityEditor";
import AvailabilityBookingPicker from "../components/AvailabilityBookingPicker";
import FounderShareCard from "../components/FounderShareCard";
import IntakeQuestionsEditor from "../components/IntakeQuestionsEditor";
import DonationCard from "../components/DonationCard";
import FounderNFTBadge from "../components/FounderNFTBadge";
import SafetyWarningDialog from "../components/SafetyWarningDialog";
import { tickProfileSession, useRatePrompt } from "../lib/ratePrompt";

const CATEGORIES = ["Readings", "Love Work", "Prosperity", "Protection", "Classes", "Initiations"];

export default function Profile() {
  const { uid } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [friends, setFriends] = useState([]);
  const [tab, setTab] = useState("posts");
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [eligibleBookings, setEligibleBookings] = useState([]);
  const [showReview, setShowReview] = useState(null);
  const [boostSvc, setBoostSvc] = useState(null);
  const [revRating, setRevRating] = useState(5);
  const [revComment, setRevComment] = useState("");
  const [svcForm, setSvcForm] = useState({ title: "", description: "", price: "", durationMin: 30, category: "Readings", imageUrl: "", intakeQuestions: [] });
  const [showAddSvc, setShowAddSvc] = useState(false);
  const [editSvcId, setEditSvcId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/users/${uid}/profile`);
      setProfile(data);
      setForm({
        name: data.user?.name || "",
        bio: data.provider?.bio ?? data.user?.bio ?? "",
        avatarUrl: data.provider?.avatarUrl ?? data.user?.avatarUrl ?? "",
        bannerUrl: data.provider?.bannerUrl ?? data.user?.bannerUrl ?? "",
        businessName: data.provider?.businessName || "",
        category: data.provider?.category || "Readings",
      });
    } catch {
      toast.error("Profile not found");
      nav("/feed");
    }
  };
  const loadPosts = async () => {
    try { const { data } = await api.get(`/users/${uid}/posts`); setPosts(data); } catch { /* noop */ }
  };
  const loadFriends = async () => {
    try { const { data } = await api.get(`/users/${uid}/friends`); setFriends(data); } catch { /* noop */ }
  };
  const loadEligible = async () => {
    if (!user || user.id === uid || profile?.user?.role !== "PROVIDER") return;
    try {
      const { data } = await api.get("/bookings");
      setEligibleBookings(data.filter((b) => b.providerId === uid && b.status === "completed" && !b.hasReview));
    } catch { /* noop */ }
  };

  useEffect(() => { load(); loadPosts(); loadFriends(); /* eslint-disable-next-line */ }, [uid]);
  useEffect(() => { loadEligible(); /* eslint-disable-next-line */ }, [uid, user, profile?.user?.role]);

  // Rate-prompt: tick the session counter once per profile mount, then attempt
  // to surface the prompt at this positive moment (eligibility is gated server-side).
  const { maybePromptAfter } = useRatePrompt();
  useEffect(() => {
    if (!user) return;
    tickProfileSession();
    const t = setTimeout(() => { maybePromptAfter("profile_visit"); }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, user?.id]);

  const save = async () => {
    try {
      // Update user (name, bio, avatar, banner) — applies to all roles
      await api.patch("/users/me", {
        name: form.name,
        bio: form.bio,
        avatarUrl: form.avatarUrl,
        bannerUrl: form.bannerUrl,
      });
      // Provider-specific fields (businessName, category) — only for providers
      if (profile.user?.role === "PROVIDER") {
        await api.put("/providers/me", {
          businessName: form.businessName,
          bio: form.bio,
          category: form.category,
          avatarUrl: form.avatarUrl,
          bannerUrl: form.bannerUrl,
        });
      }
      setEdit(false);
      await load();
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const addService = async () => {
    // Client-side validation — backend was 422'ing silently when fields
    // were empty, which read as "Failed" in the toast and felt like the
    // form didn't work.
    const title = (svcForm.title || "").trim();
    const desc = (svcForm.description || "").trim();
    const priceNum = parseFloat(svcForm.price);
    const durNum = parseInt(svcForm.durationMin);
    if (!title) return toast.error("Title is required");
    if (!desc) return toast.error("Description is required");
    if (!isFinite(priceNum) || priceNum < 0) return toast.error("Enter a valid price");
    if (!isFinite(durNum) || durNum < 1) return toast.error("Enter a valid duration");
    try {
      await api.post("/services", {
        ...svcForm,
        title,
        description: desc,
        price: priceNum,
        durationMin: durNum,
        intakeQuestions: (svcForm.intakeQuestions || []).filter((q) => (q.label || "").trim()),
      });
      setShowAddSvc(false);
      setSvcForm({ title: "", description: "", price: "", durationMin: 30, category: "Readings", imageUrl: "", intakeQuestions: [] });
      await load();
      toast.success("Service added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEditService = (s) => {
    setEditSvcId(s.id);
    setSvcForm({
      title: s.title || "",
      description: s.description || "",
      price: String(s.price ?? ""),
      durationMin: s.durationMin || 30,
      category: s.category || "Readings",
      imageUrl: s.imageUrl || "",
      intakeQuestions: Array.isArray(s.intakeQuestions) ? s.intakeQuestions : [],
    });
  };

  const cancelEditService = () => {
    setEditSvcId(null);
    setSvcForm({ title: "", description: "", price: "", durationMin: 30, category: "Readings", imageUrl: "", intakeQuestions: [] });
  };

  const saveEditService = async () => {
    if (!editSvcId) return;
    try {
      await api.patch(`/services/${editSvcId}`, {
        title: svcForm.title,
        description: svcForm.description,
        price: parseFloat(svcForm.price),
        durationMin: parseInt(svcForm.durationMin),
        category: svcForm.category,
        imageUrl: svcForm.imageUrl || null,
        intakeQuestions: (svcForm.intakeQuestions || []).filter((q) => (q.label || "").trim()),
      });
      cancelEditService();
      await load();
      toast.success("Service updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteService = async (sid, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/services/${sid}`);
      await load();
      toast.success("Service deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitReview = async () => {
    if (!showReview) return;
    try {
      await api.post("/reviews", {
        providerId: uid, bookingId: showReview.id, rating: revRating, comment: revComment,
      });
      setShowReview(null); setRevComment(""); setRevRating(5);
      await load(); await loadEligible();
      toast.success("Review posted · +50 CAUL");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const bookService = async (sid, paymentMethod = "card", slotAt = null, intakeAnswers = null, couponCode = null) => {
    try {
      const payload = { origin: window.location.origin, kind: "service", serviceId: sid, paymentMethod };
      if (slotAt) payload.slotAt = slotAt;
      if (intakeAnswers && Object.keys(intakeAnswers).length > 0) {
        payload.intakeAnswers = intakeAnswers;
      }
      if (couponCode) payload.couponCode = couponCode;
      const { data } = await api.post("/checkout/init", payload);
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!profile) return <Layout><div className="parchment p-12 text-center">Loading...</div></Layout>;

  const u = profile.user;
  const p = profile.provider;
  const isOwner = user?.id === uid;
  const isProvider = u.role === "PROVIDER";
  const isAdmin = u.role === "ADMIN";
  const canReceiveDonations = isProvider || isAdmin;
  const displayName = (isProvider && p?.businessName) || u.name;
  const avatarUrl = p?.avatarUrl || u.avatarUrl;
  const bannerUrl = p?.bannerUrl || u.bannerUrl;
  const bio = p?.bio || u.bio;
  const joinDate = u.createdAt ? new Date(u.createdAt) : null;

  const ROLE_BADGE = isProvider
    ? { label: "Practitioner", icon: Sparkles, color: "bg-primary/15 text-primary border-primary/40" }
    : u.role === "ADMIN"
      ? { label: "Admin", icon: Star, color: "bg-destructive/15 text-destructive border-destructive/40" }
      : { label: "Seeker", icon: Users, color: "bg-secondary/15 text-secondary border-secondary/40" };

  // Tabs: Posts always, About always, Friends always, Services for providers + admins, Reviews providers only
  const tabs = [
    { id: "posts", label: "Posts", icon: FileText },
    { id: "about", label: "About", icon: Users },
    { id: "friends", label: `Friends · ${profile.friendCount}`, icon: Users },
    ...(canReceiveDonations ? [
      { id: "services", label: "Services", icon: ShoppingBag },
    ] : []),
    // Owner-only Availability tab — where practitioners publish their
    // weekly recurring hours. Clients booking a service can only pick
    // slots inside these windows. Admins can access this too so they can
    // publish availability for their own services.
    ...(isOwner && (isProvider || isAdmin) ? [
      { id: "availability", label: "Availability", icon: Clock },
    ] : []),
    ...(isProvider ? [
      { id: "reviews", label: `Reviews · ${p?.totalReviews ?? 0}`, icon: Star },
    ] : []),
  ];

  return (
    <Layout>
      <SafetyWarningDialog trigger="profile" />
      {/* ───────── COVER + IDENTITY (Facebook-mobile style) ─────────
          Negative top margin cancels Layout's py-8, so the cover image
          sits flush against the top nav with no whitespace gap. */}
      <div className="parchment overflow-hidden -mx-6 lg:mx-0 lg:rounded-sm -mt-8">
        {/* Cover photo */}
        <div className="relative h-40 sm:h-56 md:h-72 bg-gradient-to-br from-primary/25 via-secondary/15 to-primary/10">
          {bannerUrl && <img src={bannerUrl} alt="" onError={hideOnError} className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent" />
          {edit && (
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
              <ImageUploader value={form.bannerUrl} onChange={(v) => setForm({ ...form, bannerUrl: v })} testid="profile-banner-upload" label={<><Camera size={14} className="inline mr-1" strokeWidth={1.5} /> Edit Cover</>} />
            </div>
          )}
        </div>

        {/* Identity block — centered on mobile, left-aligned with avatar on desktop */}
        <div className="px-4 sm:px-8 pb-5">
          {/* Avatar */}
          <div className="flex justify-center sm:justify-start -mt-16 sm:-mt-20 mb-3">
            <div className="relative">
              <ElderAvatarRing show={isProvider && !!p?.elder} index={p?.elderIndex}>
                <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-40 md:h-40 rounded-full bg-card border-4 border-card flex items-center justify-center font-serif text-5xl text-secondary overflow-hidden shadow-lg" data-testid="profile-avatar">
                  {avatarUrl ? <img src={avatarUrl} alt="" onError={hideOnError} className="w-full h-full object-cover" /> : (displayName?.[0]?.toUpperCase() || "?")}
                </div>
              </ElderAvatarRing>
              {edit && (
                <div className="absolute -bottom-1 -right-1">
                  <ImageUploader value={form.avatarUrl} onChange={(v) => setForm({ ...form, avatarUrl: v })} testid="profile-avatar-upload" label={<Camera size={14} strokeWidth={1.5} />} />
                </div>
              )}
            </div>
          </div>

          {/* Name + role + stats — centered on mobile */}
          <div className="text-center sm:text-left">
            {edit ? (
              <div className="space-y-2 max-w-md mx-auto sm:mx-0">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full bg-input border border-secondary/30 p-2 rounded-sm font-serif text-2xl text-center sm:text-left" data-testid="profile-edit-name" />
                {isProvider && (
                  <>
                    <input value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} placeholder="Business name" className="w-full bg-input border border-secondary/30 p-2 rounded-sm font-serif text-xl text-center sm:text-left" data-testid="profile-edit-business" />
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-input border border-secondary/30 p-2 rounded-sm text-sm" data-testid="profile-edit-category">
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                  <h1 className="font-serif text-3xl sm:text-4xl" data-testid="profile-display-name">{displayName}</h1>
                  {isProvider && <VerifiedBadge size={22} show={p?.verified} />}
                  {isProvider && <ElderBadge size={22} show={p?.elder} index={p?.elderIndex} />}
                  {isProvider && <VerifiedTierBadge tier={p?.verifiedTier} size={16} compact />}
                </div>
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 flex-wrap" data-testid="profile-role-row">
                  <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-sm border uppercase tracking-wider ${ROLE_BADGE.color}`} data-testid="profile-role-badge">
                    <ROLE_BADGE.icon size={12} strokeWidth={1.75} />
                    {ROLE_BADGE.label}
                  </span>
                  {isProvider && p?.category && (
                    <span className="text-xs px-2.5 py-1 rounded-sm bg-secondary/10 text-secondary/90 uppercase tracking-wider" data-testid="profile-category">
                      {p.category}
                    </span>
                  )}
                  {isProvider && p?.averageRating > 0 && (
                    <span className="flex items-center gap-1.5 text-xs text-foreground/70">
                      <StarRating value={p.averageRating} testid="profile-rating" />
                      <span className="hidden sm:inline">{p.averageRating?.toFixed(1)} · {p.totalReviews} reviews</span>
                    </span>
                  )}
                  {isProvider && <TrustScoreBadge providerId={uid} compact />}
                </div>
                <div className="text-xs text-foreground/55 mt-2 flex items-center justify-center sm:justify-start gap-2 sm:gap-3 flex-wrap">
                  <span><span className="font-serif text-foreground/80">{profile.friendCount}</span> {profile.friendCount === 1 ? "friend" : "friends"}</span>
                  <span>·</span>
                  <span><span className="font-serif text-foreground/80">{profile.postCount}</span> posts</span>
                  {joinDate && (<><span className="hidden sm:inline">·</span><span className="hidden sm:inline-flex items-center gap-1"><Calendar size={11} strokeWidth={1.5} /> Joined {joinDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span></>)}
                </div>
              </>
            )}
          </div>

          {/* Action buttons — FB mobile: primary CTA full-width, secondary icons inline */}
          <div className="mt-4 sm:mt-5">
            {isOwner ? (
              edit ? (
                <div className="flex gap-2">
                  <button onClick={save} className="flex-1 sm:flex-initial bg-primary text-primary-foreground px-5 py-2.5 rounded-sm uppercase tracking-wider text-xs flex items-center justify-center gap-2" data-testid="profile-save-btn">
                    Save Profile
                  </button>
                  <button onClick={() => { setEdit(false); load(); }} className="flex-1 sm:flex-initial border border-secondary/40 text-foreground/80 px-5 py-2.5 rounded-sm uppercase tracking-wider text-xs" data-testid="profile-cancel-btn">
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setEdit(true)} className="bg-primary text-primary-foreground px-5 py-2.5 rounded-sm uppercase tracking-wider text-xs flex items-center justify-center gap-2" data-testid="profile-edit-btn">
                    <Edit size={14} strokeWidth={1.75} /> Edit Profile
                  </button>
                  <button onClick={() => nav("/golive")} className="bg-destructive/80 hover:bg-destructive text-destructive-foreground px-5 py-2.5 rounded-sm uppercase tracking-wider text-xs flex items-center justify-center gap-2" data-testid="profile-golive-btn">
                    <span className="w-2 h-2 bg-current rounded-full" /> Go Live
                  </button>
                  {isProvider && p?.elder && (
                    <FounderShareCard
                      elder={{
                        userId: uid,
                        name: u?.name,
                        businessName: p?.businessName,
                        elderIndex: p?.elderIndex,
                        avatarUrl: p?.avatarUrl,
                        bio: p?.bio,
                        category: p?.category,
                      }}
                      trigger={
                        <button className="border border-primary/50 text-primary hover:bg-primary/10 px-5 py-2.5 rounded-sm uppercase tracking-wider text-xs flex items-center justify-center gap-2" data-testid="profile-elder-share-btn">
                          <Crown size={14} strokeWidth={1.75} /> Share Elder Badge
                        </button>
                      }
                    />
                  )}
                </div>
              )
            ) : user && (
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:items-end">
                <div className="col-span-2 sm:col-span-1 sm:flex-initial">
                  <FriendButton otherUserId={uid} />
                </div>
                <button onClick={() => nav(`/messages/${uid}`)} className="bg-secondary/15 hover:bg-secondary/25 text-foreground px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider flex items-center justify-center gap-2" data-testid="profile-message-btn">
                  <MessageSquare size={14} strokeWidth={1.5} /> Message
                </button>
                {isProvider && (
                  <>
                    <button onClick={() => nav(`/call/${uid}?type=voice`)} className="bg-secondary/15 hover:bg-secondary/25 text-foreground px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider flex items-center justify-center gap-2" data-testid="profile-call-btn">
                      <Phone size={14} strokeWidth={1.5} /> Call
                    </button>
                    <button onClick={() => nav(`/call/${uid}?type=video`)} className="bg-primary text-primary-foreground px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider flex items-center justify-center gap-2" data-testid="profile-video-btn">
                      <Video size={14} strokeWidth={1.5} /> Video
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Bio */}
          {edit ? (
            <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} placeholder="Tell people about yourself…" className="w-full bg-input border border-secondary/30 p-3 rounded-sm mt-5" data-testid="profile-edit-bio" />
          ) : (
            bio && <p className="text-foreground/80 leading-relaxed font-light text-center sm:text-left text-sm sm:text-base border-t border-secondary/15 pt-4 mt-5" data-testid="profile-bio">{bio}</p>
          )}

          {isOwner && isProvider && p?.subscriptionStatus === "active" && (
            <ConnectStripeButton providerId={uid} isOwner={isOwner} />
          )}
        </div>
      </div>

      {/* ───────── TABS (sticky on mobile) ───────── */}
      <div className="sticky top-[120px] lg:top-0 z-10 -mx-6 lg:mx-0 bg-background/95 lg:bg-transparent backdrop-blur lg:backdrop-blur-0 mt-4 mb-4 lg:mt-6 lg:mb-6 border-b border-secondary/15 lg:border-secondary/15">
        <div className="flex gap-0 px-4 lg:px-0 overflow-x-auto scroll-thin" data-testid="profile-tabs">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.18em] whitespace-nowrap font-semibold transition-colors ${tab === t.id ? "text-secondary border-b-2 border-secondary" : "text-foreground/55 hover:text-foreground border-b-2 border-transparent"}`}
              data-testid={`profile-tab-${t.id}`}>
              <t.icon size={13} strokeWidth={1.75} />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ───────── TAB CONTENT ───────── */}
      {tab === "posts" && (
        <div className="space-y-4" data-testid="profile-posts">
          {isOwner && (
            <PostComposer onPosted={(p) => setPosts([{ ...p, user: profile.user, provider: profile.provider, sponsored: false }, ...posts])} />
          )}
          {posts.length === 0 ? (
            <div className="parchment p-12 text-center text-foreground/50 italic font-serif text-xl">No posts yet.</div>
          ) : posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              onUpdate={(np) => setPosts(posts.map(x => x.id === np.id ? np : x))}
              onRemove={(pid) => setPosts((arr) => arr.filter((x) => x.id !== pid))}
            />
          ))}
        </div>
      )}

      {tab === "about" && (
        <div className="space-y-4">
          {/* Founder NFT badge — only renders if user has minted Founder NFT */}
          {isProvider && p?.elder && (
            <FounderNFTBadge userId={uid} isOwner={isOwner} />
          )}
          {/* Trust + endorsements column for providers */}
          {isProvider && isOwner && <TrustStatusCard />}
          {isProvider && (
            <div className="parchment p-5" data-testid="profile-trust-score">
              <TrustScoreBadge providerId={uid} showBreakdown />
            </div>
          )}
          {isProvider && isOwner && <TrustScoreTrendCard providerId={uid} />}
          {isProvider && isOwner && <ProviderAnalyticsCard providerId={uid} />}
          {isProvider && p?.introVideoUrl && (
            <div className="parchment p-4 space-y-2" data-testid="profile-intro-video">
              <div className="label-mystic">Intro Video</div>
              <video src={p.introVideoUrl} controls className="w-full rounded-sm border border-secondary/20" />
            </div>
          )}
          {isProvider && (p?.tradition || p?.teacher || p?.temple || p?.lineageStatement || (p?.certifications?.length > 0)) && (
            <div className="parchment p-6 space-y-3" data-testid="profile-lineage-view">
              <div className="label-mystic flex items-center gap-2"><Sparkles size={12} /> Lineage &amp; Tradition</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {p?.tradition && <div><span className="text-foreground/55">Tradition · </span><span className="text-foreground/90">{p.tradition}</span></div>}
                {p?.teacher && <div><span className="text-foreground/55">Teacher · </span><span className="text-foreground/90">{p.teacher}</span></div>}
                {p?.temple && <div><span className="text-foreground/55">Temple · </span><span className="text-foreground/90">{p.temple}</span></div>}
                {p?.yearsInitiated != null && <div><span className="text-foreground/55">Years initiated · </span><span className="text-foreground/90">{p.yearsInitiated}</span></div>}
              </div>
              {p?.lineageStatement && (
                <p className="text-foreground/80 italic font-serif text-base leading-relaxed pt-2 border-t border-secondary/10">"{p.lineageStatement}"</p>
              )}
              {p?.certifications?.length > 0 && (
                <div className="pt-2 border-t border-secondary/10 space-y-1">
                  <div className="label-mystic">Certifications</div>
                  {p.certifications.map((c, i) => (
                    <div key={i} className="text-sm text-foreground/80">· {c}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isProvider && isOwner && edit && (
            <LineageEditor profile={p} onSaved={() => load()} />
          )}
          {isProvider && !isOwner && (user?.role === "PROVIDER" || user?.role === "ADMIN") && (
            <div className="flex justify-end">
              <EndorseDialog providerId={uid} providerName={displayName} onDone={load} />
            </div>
          )}
          <ProfileEndorsementsList providerId={uid} />
          <div className="parchment p-6 space-y-4" data-testid="profile-about">
            <Row icon={Sparkles} label="Role" value={ROLE_BADGE.label} />
            {isProvider && p?.category && <Row icon={ShoppingBag} label="Category" value={p.category} />}
            {isProvider && p?.performanceScore != null && (
              <Row icon={Star} label="Performance Score" value={`${(p.performanceScore * 100).toFixed(0)} / 100`} />
            )}
            <Row icon={Users} label="Friends" value={profile.friendCount} />
            <Row icon={FileText} label="Posts" value={profile.postCount} />
            {joinDate && <Row icon={Calendar} label="Joined Cauldron" value={joinDate.toLocaleDateString(undefined, { dateStyle: "long" })} />}
            {bio && (
              <div className="border-t border-secondary/15 pt-4">
                <div className="label-mystic mb-2">Bio</div>
                <p className="text-foreground/80 leading-relaxed">{bio}</p>
              </div>
            )}
          </div>
          {isOwner && (
            <div className="parchment p-6 border-destructive/30" data-testid="profile-danger-zone">
              <div className="label-mystic text-destructive mb-2">Danger Zone</div>
              <p className="text-xs text-foreground/65 mb-3">
                Permanently delete your Cauldron account and all associated data. This cannot be undone.
              </p>
              <a
                href="/account-deletion"
                className="inline-block text-xs uppercase tracking-[0.18em] text-destructive hover:underline"
                data-testid="profile-delete-account-link"
              >
                Delete my account →
              </a>
            </div>
          )}
        </div>
      )}

      {tab === "friends" && (
        <div data-testid="profile-friends-grid">
          {friends.length === 0 ? (
            <div className="parchment p-12 text-center text-foreground/50 italic font-serif text-xl">No friends yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {friends.map((f) => (
                <button key={f.id} onClick={() => nav(`/profile/${f.id}`)}
                  className="parchment p-4 text-left hover:border-secondary/40 transition-colors"
                  data-testid={`profile-friend-${f.id}`}>
                  <div className="w-full aspect-square rounded-sm bg-secondary/15 border border-secondary/40 flex items-center justify-center font-serif text-3xl text-secondary mb-2 overflow-hidden">
                    {f.avatarUrl ? <img src={f.avatarUrl} alt="" onError={hideOnError} className="w-full h-full object-cover" /> : (f.name?.[0]?.toUpperCase() || "?")}
                  </div>
                  <div className="font-serif text-sm truncate">{f.name}</div>
                  <div className="label-mystic">{f.role}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "services" && canReceiveDonations && (
        <div className="space-y-4" data-testid="profile-services">
          {/* Donation card — visible to everyone; goal/progress + form for visitors, goal-editor for owner */}
          <DonationCard
            recipientId={uid}
            recipientName={displayName}
            viewerSignedIn={!!user}
            isOwner={isOwner}
          />
          {/* Gating: ADMIN always sees the button. PROVIDER only sees it
              when their $49.99/mo subscription is active. Unsubscribed
              practitioners see a paywall card pointing them at /subscribe
              so they understand WHY the button is missing — invisible
              gates are the most confusing UX failure mode. */}
          {isOwner && isAdmin && (
            <button onClick={() => setShowAddSvc(!showAddSvc)} className="parchment p-4 w-full text-left hover:border-secondary/40" data-testid="profile-add-service">
              + Add a Service
            </button>
          )}
          {isOwner && isProvider && p?.subscriptionStatus === "active" && (
            <button onClick={() => setShowAddSvc(!showAddSvc)} className="parchment p-4 w-full text-left hover:border-secondary/40" data-testid="profile-add-service">
              + Add a Service
            </button>
          )}
          {isOwner && isProvider && p?.subscriptionStatus !== "active" && (
            <Link
              to="/subscribe"
              className="parchment p-4 w-full text-left hover:border-secondary/40 block border border-amber-500/30"
              data-testid="profile-subscribe-to-list"
            >
              <div className="label-mystic text-amber-300 mb-1">Subscription required</div>
              <div className="text-sm">
                Activate your <b>$49.99/mo Practitioner subscription</b> to publish services and start receiving paid bookings.
              </div>
              <div className="text-xs text-secondary mt-2 uppercase tracking-[0.18em]">
                Subscribe now →
              </div>
            </Link>
          )}
          {showAddSvc && (
            <div className="parchment p-6 space-y-3">
              <input placeholder="Service title" value={svcForm.title} onChange={(e) => setSvcForm({ ...svcForm, title: e.target.value })} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="svc-title" />
              <textarea placeholder="Description" value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="svc-desc" />
              <div className="grid grid-cols-3 gap-3">
                <input type="number" step="0.01" placeholder="Price USD" value={svcForm.price} onChange={(e) => setSvcForm({ ...svcForm, price: e.target.value })} className="bg-input border border-secondary/20 p-3 rounded-sm" data-testid="svc-price" />
                <input type="number" placeholder="Duration min" value={svcForm.durationMin} onChange={(e) => setSvcForm({ ...svcForm, durationMin: e.target.value })} className="bg-input border border-secondary/20 p-3 rounded-sm" data-testid="svc-duration" />
                <CategorySelect value={svcForm.category} onChange={(v) => setSvcForm({ ...svcForm, category: v })} testId="svc-cat" />
              </div>
              <IntakeQuestionsEditor
                value={svcForm.intakeQuestions}
                onChange={(qs) => setSvcForm({ ...svcForm, intakeQuestions: qs })}
                testIdPrefix="svc-intake"
              />
              <button onClick={addService} className="bg-primary text-primary-foreground px-6 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid="svc-create-btn">Create</button>
            </div>
          )}
          {profile.services?.map((s) => (
            <div key={s.id} className="parchment p-6 flex flex-col md:flex-row items-start justify-between gap-4" data-testid={`service-${s.id}`}>
              {editSvcId === s.id ? (
                <div className="flex-1 w-full space-y-3" data-testid={`service-edit-form-${s.id}`}>
                  <div className="label-mystic text-secondary">Editing service</div>
                  <input placeholder="Service title" value={svcForm.title} onChange={(e) => setSvcForm({ ...svcForm, title: e.target.value })} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid={`svc-edit-title-${s.id}`} />
                  <textarea placeholder="Description" value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" rows={3} data-testid={`svc-edit-desc-${s.id}`} />
                  <div className="grid grid-cols-3 gap-3">
                    <input type="number" step="0.01" placeholder="Price USD" value={svcForm.price} onChange={(e) => setSvcForm({ ...svcForm, price: e.target.value })} className="bg-input border border-secondary/20 p-3 rounded-sm" data-testid={`svc-edit-price-${s.id}`} />
                    <input type="number" placeholder="Duration min" value={svcForm.durationMin} onChange={(e) => setSvcForm({ ...svcForm, durationMin: e.target.value })} className="bg-input border border-secondary/20 p-3 rounded-sm" data-testid={`svc-edit-duration-${s.id}`} />
                    <CategorySelect value={svcForm.category} onChange={(v) => setSvcForm({ ...svcForm, category: v })} testId={`svc-edit-cat-${s.id}`} />
                  </div>
                  <IntakeQuestionsEditor
                    value={svcForm.intakeQuestions}
                    onChange={(qs) => setSvcForm({ ...svcForm, intakeQuestions: qs })}
                    testIdPrefix={`svc-edit-intake-${s.id}`}
                  />
                  <div className="flex gap-2">
                    <button onClick={saveEditService} className="bg-primary text-primary-foreground px-5 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`svc-edit-save-${s.id}`}>Save</button>
                    <button onClick={cancelEditService} className="border border-foreground/20 text-foreground/70 px-5 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`svc-edit-cancel-${s.id}`}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1">
                    <div className="label-mystic">{s.category} · {s.durationMin}min</div>
                    <h3 className="font-serif text-2xl mt-1">{s.title}</h3>
                    <p className="text-foreground/75 text-sm mt-2">{s.description}</p>
                    {s.availableSlots && s.availableSlots.length > 0 && !isOwner && user && (
                      <BookingSlotPicker slots={s.availableSlots} serviceId={s.id} onPick={(slot) => bookService(s.id, "card", slot)} />
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-3xl text-secondary">${s.price}</div>
                    {!isOwner && user && (
                      <div className="mt-3 flex flex-col gap-2 items-end">
                        <AvailabilityBookingPicker
                          providerId={uid}
                          serviceId={s.id}
                          serviceTitle={s.title}
                          durationMin={s.durationMin}
                          intakeQuestions={s.intakeQuestions}
                          onPick={(slot, answers, couponCode) => bookService(s.id, "card", slot, answers, couponCode)}
                          onCryptoBook={(slot, answers, couponCode) => bookService(s.id, "crypto", slot, answers, couponCode)}
                        />
                      </div>
                    )}
                    {isOwner && (
                      <div className="mt-3 flex gap-2 items-end justify-end flex-wrap" data-testid={`service-owner-actions-${s.id}`}>
                        <button onClick={() => setBoostSvc(s)} className="inline-flex items-center gap-1.5 border border-secondary text-secondary hover:bg-secondary/10 px-4 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`service-boost-${s.id}`}>
                          <Zap size={12} strokeWidth={1.75} /> Boost
                        </button>
                        <button onClick={() => startEditService(s)} className="inline-flex items-center gap-1.5 border border-secondary/40 text-secondary hover:bg-secondary/10 px-4 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`service-edit-${s.id}`}>
                          <Edit size={12} strokeWidth={1.75} /> Edit
                        </button>
                        <button onClick={() => deleteService(s.id, s.title)} className="inline-flex items-center gap-1.5 border border-destructive/40 text-destructive hover:bg-destructive/10 px-4 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`service-delete-${s.id}`}>
                          <Trash2 size={12} strokeWidth={1.75} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
          {isProvider && (!profile.services || profile.services.length === 0) && <div className="parchment p-8 text-foreground/50 italic font-serif text-xl text-center">No services yet.</div>}
        </div>
      )}

      {/* ───────── AVAILABILITY (owner-only) ───────── */}
      {tab === "availability" && isOwner && (isProvider || isAdmin) && (
        <div className="space-y-4" data-testid="profile-availability">
          <AvailabilityEditor />
        </div>
      )}

      {tab === "reviews" && isProvider && (
        <div className="space-y-4" data-testid="profile-reviews">
          {!isOwner && user && eligibleBookings.length > 0 && !showReview && (
            <div className="parchment p-4 border-secondary/40">
              <div className="label-mystic mb-2">Sessions awaiting your review</div>
              <div className="space-y-2">
                {eligibleBookings.map((b) => (
                  <button key={b.id} onClick={() => setShowReview(b)}
                    className="w-full text-left p-3 hover:bg-secondary/5 border border-secondary/15 rounded-sm flex justify-between items-center"
                    data-testid={`review-eligible-${b.id}`}>
                    <span>{b.service?.title} · {new Date(b.completedAt || b.createdAt).toLocaleDateString()}</span>
                    <span className="text-secondary text-sm">Review →</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {showReview && (
            <div className="parchment p-6 space-y-3" data-testid="review-form">
              <div className="label-mystic">Reviewing: {showReview.service?.title}</div>
              <ReviewComposer
                booking={showReview}
                onSubmitted={() => { setShowReview(null); load(); }}
              />
              <button onClick={() => setShowReview(null)} className="text-foreground/60 px-4 py-2 text-xs uppercase tracking-wider" data-testid="review-cancel">Cancel</button>
            </div>
          )}
          {profile.reviews?.map((r) => (
            <div key={r.id} className="parchment p-6 space-y-3" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-serif text-lg">{r.clientName}</span>
                <StarRating value={r.rating} testid={`review-stars-${r.id}`} />
              </div>
              {r.comment && <p className="text-foreground/80 italic font-serif text-lg leading-relaxed">"{r.comment}"</p>}
              {r.testimonialUrl && r.testimonialKind === "video" && (
                <video src={r.testimonialUrl} controls className="w-full max-w-md rounded-sm border border-secondary/20" data-testid={`review-video-${r.id}`} />
              )}
              {r.testimonialUrl && r.testimonialKind === "audio" && (
                <audio src={r.testimonialUrl} controls className="w-full max-w-md" data-testid={`review-audio-${r.id}`} />
              )}
              {r.axes && Object.keys(r.axes).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 pt-2 text-[11px]" data-testid={`review-axes-${r.id}`}>
                  {Object.entries(r.axes).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-foreground/60">
                      <span className="capitalize">{k.replace(/([A-Z])/g, ' $1').trim()}</span>
                      <span className="text-secondary/90">{v}/5</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="label-mystic">{new Date(r.createdAt).toLocaleDateString()}</div>
            </div>
          ))}
          {(!profile.reviews || profile.reviews.length === 0) && <div className="parchment p-8 text-foreground/50 italic font-serif text-xl text-center">No reviews yet.</div>}
        </div>
      )}
      <BoostDialog
        service={boostSvc}
        open={!!boostSvc}
        onClose={() => setBoostSvc(null)}
        onBoosted={() => { load(); setBoostSvc(null); }}
      />
    </Layout>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3" data-testid={`about-${label.toLowerCase().replace(/ /g, "-")}`}>
      <Icon size={16} strokeWidth={1.5} className="text-secondary/80 shrink-0" />
      <div className="label-mystic w-40 shrink-0">{label}</div>
      <div className="text-foreground/85">{value}</div>
    </div>
  );
}

function ProfileEndorsementsList({ providerId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get(`/endorsements/${providerId}`).then(({ data }) => {
      if (!cancelled) setRows(data.endorsements || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [providerId]);
  if (!rows.length) return null;
  return (
    <div className="parchment p-6 space-y-3" data-testid="profile-endorsements">
      <div className="label-mystic flex items-center gap-2">
        <Sparkles size={12} className="text-violet-300" /> Endorsements ({rows.length})
      </div>
      {rows.map((r) => (
        <div key={r.id} className="border-t border-secondary/10 pt-3 first:border-t-0 first:pt-0" data-testid={`endorsement-${r.id}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="font-serif text-base">
              {r.endorserBusinessName || r.endorser?.name}
              {r.endorserIsElder && <span className="ml-1.5 text-secondary text-[10px] uppercase tracking-wider">· Elder</span>}
              {r.endorserTierAtTime === "initiate" && <span className="ml-1.5 text-violet-300 text-[10px] uppercase tracking-wider">· Initiate</span>}
            </span>
            <span className="text-[10px] text-foreground/45">{new Date(r.createdAt).toLocaleDateString()}</span>
          </div>
          {r.statement && <p className="text-foreground/75 italic text-sm leading-relaxed">"{r.statement}"</p>}
          {r.endorserTradition && <div className="text-[11px] text-foreground/45 mt-1">{r.endorserTradition}</div>}
        </div>
      ))}
    </div>
  );
}
